import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import { C } from "sj-ui-toolkit/src/atlas/utils";

import S from "./AspenWelcome.module.scss";

const TRY_EXAMPLE_CAPTION_ID = "aspen-try-example-caption";

const CAPABILITIES = [
  "Add users to an assignment — one person or a whole list.",
  "Tell you who's in an assignment and where each person stands — in progress, not started, completed, or abandoned.",
  "Show how many lessons each user has completed inside that assignment.",
];
const HERO_CAPABILITIES = [
  "Add users to an assignment — one person or a whole list.",
  "Tell you who's in an assignment and where each person stands.",
  "Break status down — in progress, not started, completed, or abandoned.",
  "Show how many lessons each user has completed inside that assignment.",
];

const INTRO =
  "take the busywork out of assignments, so you can spend your time on the learners who need you.";
const EYEBROW = "Here's what I can do today";
const FOOTER = "More capabilities are coming — I'll tell you here as they land.";

interface AspenWelcomeProps {
  layout?: "hero" | "message";
  greetingName?: string;
  disabled?: boolean;
  assignmentName: string | null;
  onTryExample: () => void;
}

export function AspenWelcome({
  layout = "message",
  greetingName,
  disabled = false,
  assignmentName,
  onTryExample,
}: AspenWelcomeProps) {
  const isHero = layout === "hero";
  const hi = greetingName ? `Hi ${greetingName}` : "Hi there";
  const greeting = isHero
    ? `${hi} — I'm here to ${INTRO}`
    : disabled
      ? `${hi} — I'm Aspen. Once I'm turned on, I'll ${INTRO}`
      : `${hi} — I'm Aspen. I'm here to ${INTRO}`;
  const caption = isHero
    ? assignmentName
      ? `I'll pull real numbers for the ${assignmentName}. More capabilities are coming soon.`
      : "More capabilities are coming soon."
    : "Available once Aspen is enabled";
  const showExampleAction = isHero || disabled;
  const capabilities = isHero ? HERO_CAPABILITIES : CAPABILITIES;

  return (
    <div
      className={C(S.welcome, isHero && S.hero, disabled && S.disabled)}
      data-testid="aspen-chat-welcome"
    >
      {isHero && (
        <h1 className={C("atlas-h2", S.heading)}>
          Welcome to Aspen
          <Tag value="Beta" className={S.betaTag} />
        </h1>
      )}

      <div className={S.row}>
        <div className={S.avatar} aria-hidden="true">
          <i className="pi pi-sparkles text-surface-950" style={{ fontSize: "0.65rem" }} />
        </div>

        <div className={S.body}>
          {!isHero && (
            <div className={S.author}>
              <span className="text-sm font-semibold">Aspen</span>
              <Tag value="Beta" className={S.betaTag} />
            </div>
          )}

          <div className={S.bubble}>
            <p>{greeting}</p>

            <h2 className={C("atlas-h5", S.eyebrow)}>{EYEBROW}</h2>
            <ol className={C(S.capabilities, isHero && S.grid)}>
              {capabilities.map((text, index) => (
                <li key={text}>
                  <span className={S.stepNumber} aria-hidden="true">
                    {index + 1}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ol>

            {showExampleAction && (
              <div className={S.actions}>
                <Button
                  label="Try an example"
                  size="small"
                  onClick={onTryExample}
                  disabled={disabled}
                  aria-describedby={TRY_EXAMPLE_CAPTION_ID}
                  data-testid="aspen-chat-try-example"
                />
                <span id={TRY_EXAMPLE_CAPTION_ID} className={S.caption}>
                  {caption}
                </span>
              </div>
            )}

            {!isHero && <p className={S.footer}>{FOOTER}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
